import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import CountUp from 'react-countup';
import { useLanguage } from '../contexts/LanguageContext';

const CARD_INITIAL = { opacity: 0, y: 30 };
const CARD_VISIBLE = { opacity: 1, y: 0 };

const VALUE_INITIAL = { scale: 0 };
const VALUE_VISIBLE = { scale: 1 };

const Stats = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const stats = [
    { id: 'clients', value: 25, suffix: '+', label: t.stats.clients },
    { id: 'ads', value: 10, prefix: '$', suffix: 'M+', label: t.stats.adsManaged },
    { id: 'projects', value: 200, suffix: '+', label: t.stats.projects },
    { id: 'experience', value: 9, suffix: '+', label: t.stats.experience }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {stats.map((stat, idx) => {
            const cardTransition = { delay: idx * 0.2, duration: 0.6 };
            const valueTransition = { delay: idx * 0.2 + 0.3, type: 'spring', stiffness: 200 };
            return (
              <motion.div
                key={stat.id}
                initial={CARD_INITIAL}
                animate={isInView ? CARD_VISIBLE : CARD_INITIAL}
                transition={cardTransition}
                className="relative group"
              >
                <div className="relative p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/20">
                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-orange-500/0 to-purple-500/0 group-hover:from-blue-500/10 group-hover:via-orange-500/10 group-hover:to-purple-500/10 rounded-2xl transition-all duration-300" />

                  <div className="relative text-center">
                    <motion.div
                      initial={VALUE_INITIAL}
                      animate={isInView ? VALUE_VISIBLE : VALUE_INITIAL}
                      transition={valueTransition}
                      className="text-5xl md:text-6xl font-bold mb-3"
                    >
                      <span className="bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent">
                        {stat.prefix}
                        {isInView && (
                          <CountUp
                            start={0}
                            end={stat.value}
                            duration={2.5}
                            delay={idx * 0.2}
                          />
                        )}
                        {stat.suffix}
                      </span>
                    </motion.div>
                    <p className="text-gray-300 text-lg font-medium">{stat.label}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Stats;
